import React, { Component, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
	StyleSheet,
	Text,
	View,
	Animated,
	TouchableOpacity,
	Platform
} from "react-native";
import { ActionButtonItem } from "./ActionButtonItem";
import {
	shadowStyle,
	alignItemsMap,
	getTouchableComponent,
	isAndroid,
	touchableBackground,
	DEFAULT_ACTIVE_OPACITY
} from "./shared";

const TextPropTypes = {
	style: () => {}
};

const ActionButton = (props = {}) => {
	const {
		resetToken = null,
		active: isActive = false,
		bgColor = "transparent",
		bgOpacity = 1,
		buttonColor = "rgba(0,0,0,1)",
		spacing = 20,
		outRangeScale = 1,
		autoInactive = true,
		onPress = () => {},
		onPressIn = () => {},
		onPressOut = () => {},
		backdrop = false,
		degrees = 45,
		position = "right",
		offsetX = 30,
		offsetY = 30,
		size = 56,
		verticalOrientation = "up",
		backgroundTappable = false,
		useNativeFeedback = true,
		activeOpacity = DEFAULT_ACTIVE_OPACITY,
		fixNativeFeedbackRadius = false,
		nativeFeedbackRippleColor = "rgba(255,255,255,0.75)",
		children
	} = props;
	const [, setResetToken] = useState(resetToken);
	const [active, setActive] = useState(isActive);
	const anim = useRef(new Animated.Value(isActive ? 1 : 0));
	const timeout = useRef(null);
	const mounted = useRef(false);

	useEffect(() => {
		mounted.current = true;

		return () => {
			mounted.current = false;
			timeout.current && clearTimeout(timeout.current);
		};
	}, []);

	useEffect(() => {
		if (isActive) {
			Animated.spring(anim.current, {
				toValue: 1,
				useNativeDriver: false
			}).start();
			setActive(true);
			setResetToken(resetToken);
		} else {
			props.onReset && props.onReset();

			Animated.spring(anim.current, {
				toValue: 0,
				useNativeDriver: false
			}).start();
			timeout.current = setTimeout(() => {
				setActive(false);
				setResetToken(resetToken);
			}, 250);
		}
	}, [resetToken, isActive]);

	//////////////////////
	// STYLESHEET GETTERS
	//////////////////////

	const getOrientation = () => {
		return { alignItems: alignItemsMap[position] };
	};

	const getOffsetXY = () => {
		return {
			// paddingHorizontal: offsetX,
			paddingVertical: offsetY
		};
	};

	const getOverlayStyles = () => {
		return [
			styles.overlay,
			{
				elevation: props.elevation,
				zIndex: props.zIndex,
				justifyContent: verticalOrientation === "up" ? "flex-end" : "flex-start"
			}
		];
	};

	const _renderMainButton = () => {
		const animatedViewStyle = {
			transform: [
				{
					scale: anim.current?.interpolate({
						inputRange: [0, 1],
						outputRange: [1, outRangeScale]
					})
				},
				{
					rotate: anim.current?.interpolate({
						inputRange: [0, 1],
						outputRange: ["0deg", degrees + "deg"]
					})
				}
			]
		};

		const wrapperStyle = {
			backgroundColor: anim.current?.interpolate({
				inputRange: [0, 1],
				outputRange: [buttonColor, props.btnOutRange || buttonColor]
			}),
			width: size,
			height: size,
			borderRadius: size / 2
		};

		const buttonStyle = {
			width: size,
			height: size,
			borderRadius: size / 2,
			alignItems: "center",
			justifyContent: "center"
		};

		const Touchable = getTouchableComponent(useNativeFeedback);
		const parentStyle =
			isAndroid && fixNativeFeedbackRadius
				? {
						right: offsetX,
						zIndex: props.zIndex,
						borderRadius: size / 2,
						width: size
				  }
				: { marginHorizontal: offsetX, zIndex: props.zIndex };

		return (
			<View
				style={[
					parentStyle,
					!props.hideShadow && Platform.OS !== "web" && shadowStyle,
					!props.hideShadow && Platform.OS !== "web" && props.shadowStyle
				]}
			>
				<Touchable
					testID={props.testID}
					accessible={props.accessible}
					accessibilityLabel={props.accessibilityLabel}
					background={touchableBackground(
						nativeFeedbackRippleColor,
						fixNativeFeedbackRadius
					)}
					activeOpacity={activeOpacity}
					onLongPress={props.onLongPress}
					onPress={() => {
						onPress();
						if (children) animateButton();
					}}
					onPressIn={onPressIn}
					onPressOut={onPressOut}
				>
					<Animated.View style={wrapperStyle}>
						<Animated.View style={[buttonStyle, animatedViewStyle]}>
							{_renderButtonIcon()}
						</Animated.View>
					</Animated.View>
				</Touchable>
			</View>
		);
	};

	const _renderButtonIcon = () => {
		const { icon, renderIcon, btnOutRangeTxt, buttonTextStyle, buttonText } =
			props;
		if (renderIcon) return renderIcon(active);
		if (icon) {
			console.warn(
				"react-native-action-button: The `icon` prop is deprecated! Use `renderIcon` instead."
			);
			return icon;
		}

		const textColor = buttonTextStyle.color || "rgba(255,255,255,1)";

		return (
			<Animated.Text
				style={[
					styles.btnText,
					buttonTextStyle,
					{
						color: anim.current?.interpolate({
							inputRange: [0, 1],
							outputRange: [textColor, btnOutRangeTxt || textColor]
						})
					}
				]}
			>
				{buttonText}
			</Animated.Text>
		);
	};

	const _renderActions = () => {
		if (!active) return null;

		let actionButtons = !Array.isArray(children) ? [children] : children;

		// Skip the children not rendered (false, null, undefined): typeof null is
		// "object", which displayed an extra item copying the parent props
		actionButtons = actionButtons.filter(actionButton =>
			React.isValidElement(actionButton)
		);

		const actionStyle = {
			flex: 1,
			alignSelf: "stretch",
			// backgroundColor: 'purple',
			justifyContent: verticalOrientation === "up" ? "flex-end" : "flex-start",
			paddingTop: verticalOrientation === "down" ? spacing : 0,
			zIndex: props.zIndex
		};

		return (
			<View style={actionStyle} pointerEvents={"box-none"}>
				{actionButtons.map((childItem, idx) => (
					<ActionButtonItem
						key={idx}
						anim={anim.current}
						{...props}
						{...(childItem?.props || {})}
						parentSize={size}
						btnColor={props.btnOutRange}
						onPress={() => {
							if (autoInactive) {
								timeout.current = setTimeout(reset, 200);
							}
							childItem?.props?.onPress();
						}}
					/>
				))}
			</View>
		);
	};

	const _renderTappableBackground = () => {
		return (
			<TouchableOpacity
				activeOpacity={1}
				style={getOverlayStyles()}
				onPress={reset}
			/>
		);
	};

	//////////////////////
	// Animation Methods
	//////////////////////

	const animateButton = (animate = true) => {
		if (active) return reset(animate);

		if (animate) {
			Animated.spring(anim.current, {
				toValue: 1,
				useNativeDriver: false
			}).start();
		} else {
			anim.current.setValue(1);
		}

		setActive(true);
	};

	const reset = (animate = true) => {
		if (props.onReset) props.onReset();

		if (animate) {
			Animated.spring(anim.current, {
				toValue: 0,
				useNativeDriver: false
			}).start();
		} else {
			anim.current.setValue(0);
		}

		timeout.current = setTimeout(() => {
			if (mounted.current) {
				setActive(false);
			}
		}, 250);
	};

	return (
		<View pointerEvents="box-none" style={[getOverlayStyles(), props.style]}>
			<Animated.View
				pointerEvents="none"
				style={[
					getOverlayStyles(),
					{
						backgroundColor: bgColor,
						opacity: anim.current?.interpolate({
							inputRange: [0, 1],
							outputRange: [0, bgOpacity]
						})
					}
				]}
			>
				{backdrop}
			</Animated.View>
			<View
				pointerEvents="box-none"
				style={[getOverlayStyles(), getOrientation(), getOffsetXY()]}
			>
				{active && !backgroundTappable && _renderTappableBackground()}

				{verticalOrientation === "up" && children && _renderActions()}
				{_renderMainButton()}
				{verticalOrientation === "down" && children && _renderActions()}
			</View>
		</View>
	);
};

ActionButton.Item = ActionButtonItem;

ActionButton.propTypes = {
	resetToken: PropTypes.any,
	active: PropTypes.bool,

	position: PropTypes.string,
	elevation: PropTypes.number,
	zIndex: PropTypes.number,

	hideShadow: PropTypes.bool,
	shadowStyle: PropTypes.oneOfType([
		PropTypes.object,
		PropTypes.array,
		PropTypes.number
	]),

	renderIcon: PropTypes.func,

	bgColor: PropTypes.string,
	bgOpacity: PropTypes.number,
	buttonColor: PropTypes.string,
	buttonTextStyle: TextPropTypes.style,
	buttonText: PropTypes.string,

	offsetX: PropTypes.number,
	offsetY: PropTypes.number,
	spacing: PropTypes.number,
	size: PropTypes.number,
	autoInactive: PropTypes.bool,
	onPress: PropTypes.func,
	onPressIn: PropTypes.func,
	onPressOut: PropTypes.func,
	backdrop: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
	degrees: PropTypes.number,
	verticalOrientation: PropTypes.oneOf(["up", "down"]),
	backgroundTappable: PropTypes.bool,
	activeOpacity: PropTypes.number,

	useNativeFeedback: PropTypes.bool,
	fixNativeFeedbackRadius: PropTypes.bool,
	nativeFeedbackRippleColor: PropTypes.string,

	testID: PropTypes.string,
	accessibilityLabel: PropTypes.string,
	accessible: PropTypes.bool
};

const styles = StyleSheet.create({
	overlay: {
		position: "absolute",
		bottom: 0,
		left: 0,
		right: 0,
		top: 0,
		backgroundColor: "transparent"
	},
	btnText: {
		marginTop: -4,
		fontSize: 24,
		backgroundColor: "transparent"
	}
});

export default ActionButton;
